import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Input,
  Select,
  DatePicker
} from 'antd';
import {
  BookOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";
import withRoleAccess from '../../hoc/withRoleAccess';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const INVOICE_OPTIONS = [
  { value: 'preparing', label: 'Preparing', color: '#d46b08', bg: '#fff7e6' },
  { value: 'sent', label: 'Sent', color: '#389e0d', bg: '#f6ffed' },
];
const STATUS_OPTIONS = [
  { value: 'continue', label: 'Continue', color: '#1d39c4', bg: '#f0f5ff' },
  { value: 'discontinue', label: 'Discontinue', color: '#cf1322', bg: '#fff1f0' },
  { value: 'paid', label: 'Paid', color: '#531dab', bg: '#f9f0ff' },
];
const EVALUATION_OPTIONS = INVOICE_OPTIONS;

const LOOKBACK_OPTIONS = [
  { value: 'upcoming', label: 'Ending soon (next 21 days)' },
  { value: '30', label: 'Ended in last 30 days' },
  { value: '60', label: 'Ended in last 60 days' },
  { value: '90', label: 'Ended in last 90 days' },
  { value: '180', label: 'Ended in last 180 days' },
  { value: 'all', label: 'All ended courses' },
];

const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta",
};

const rowKey = (record) => `${record.student_id}_${record.groupid}`;

// "by Nickname · 12 Oct 14:30" under a dropdown / remark
const ChangedBy = ({ name, at, action }) => {
  if (!name && !at) return null;
  return (
    <div style={{ lineHeight: 1.3 }}>
      {name && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
          {action ? `${action} by` : 'by'} {name}
        </Text>
      )}
      {at && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
          {moment(at).format('DD MMM YYYY HH:mm')}
        </Text>
      )}
    </div>
  );
};

// Keeps its own text while typing and saves on blur
const RemarkCell = ({ value, onSave }) => {
  const [text, setText] = useState(value || '');

  useEffect(() => {
    setText(value || '');
  }, [value]);

  return (
    <TextArea
      autoSize={{ minRows: 1, maxRows: 4 }}
      value={text}
      placeholder="Add remark"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== (value || '')) onSave(text);
      }}
    />
  );
};

const StudentClassesReminder = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [invoiceFilter, setInvoiceFilter] = useState(null);
  const [evaluationFilter, setEvaluationFilter] = useState(null);
  const [lookbackDays, setLookbackDays] = useState('60');
  // [from, to] as 'YYYY-MM-DD'; when set it replaces the lookback limit
  const [endDateRange, setEndDateRange] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const token = localStorage.getItem("token");
  let userRole = null;
  if (token) {
    try {
      userRole = jwtDecode(token).role;
    } catch (err) {
      console.error("Invalid token", err);
    }
  }
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchCourseEnd = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/student-reminder/course-end`, {
        params: { lookbackDays: endDateRange ? 'all' : lookbackDays },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRows(response.data);
    } catch (error) {
      console.error('❌ Error fetching course end list:', error);
      message.error('Failed to fetch course end list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookbackDays, endDateRange]);

  const saveField = async (record, field, value) => {
    const key = `${rowKey(record)}_${field}`;
    setSavingKey(key);
    try {
      const response = await axios.put(
        `${API_URL}/student-reminder/course-end/${record.student_id}/${record.groupid}`,
        { field, value: value ?? null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setRows(prev => prev.map(r =>
        rowKey(r) === rowKey(record) ? { ...r, ...response.data } : r
      ));
    } catch (error) {
      console.error('❌ Error saving:', error);
      message.error('Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  const branches = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => {
      if (r.branch_id && !seen.has(r.branch_id)) {
        seen.set(r.branch_id, r.branch_name || `Branch ${r.branch_id}`);
      }
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const lower = searchText.toLowerCase();
    // 'none' matches rows with no value yet
    const matches = (filter, value) =>
      !filter || (filter === 'none' ? !value : value === filter);
    return rows.filter(r => {
      if (selectedBranch && r.branch_id !== selectedBranch) return false;
      if (!matches(statusFilter, r.status)) return false;
      if (!matches(invoiceFilter, r.invoice_status)) return false;
      if (!matches(evaluationFilter, r.evaluation_status)) return false;
      if (endDateRange && (r.end_date < endDateRange[0] || r.end_date > endDateRange[1])) return false;
      if (!lower) return true;
      return [r.firstname, r.lastname, r.nickname, r.class_code, r.remark]
        .some(v => v?.toLowerCase().includes(lower));
    });
  }, [rows, searchText, selectedBranch, statusFilter, invoiceFilter, evaluationFilter, endDateRange]);

  const renderSelect = (record, field, valueKey, options, placeholder) => {
    const selected = options.find(o => o.value === record[valueKey]);
    return (
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Select
          size="small"
          allowClear
          placeholder={placeholder}
          className="course-end-select"
          style={{
            width: '100%',
            '--select-bg': selected?.bg || '#fff',
            '--select-color': selected?.color || '#d9d9d9',
          }}
          value={record[valueKey] || undefined}
          loading={savingKey === `${rowKey(record)}_${field}`}
          onChange={(val) => saveField(record, field, val)}
          options={options.map(o => ({
            value: o.value,
            label: <span style={{ color: o.color, fontWeight: 500 }}>{o.label}</span>,
          }))}
        />
        <ChangedBy
          name={record[`${field}_by_name`]}
          at={record[`${field}_at`]}
          action={selected?.label}
        />
      </Space>
    );
  };

  const columns = [
    {
      title: t("Branch"),
      key: "branch",
      width: 110,
      render: (_, record) => record.branch_name ? (
        <Tag color={branchColors[record.branch_id] || "default"}>{record.branch_name}</Tag>
      ) : (
        <Tag>-</Tag>
      ),
    },
    {
      title: 'End Date',
      key: 'end_date',
      width: 120,
      sorter: (a, b) => a.end_date.localeCompare(b.end_date),
      defaultSortOrder: 'ascend',
      render: (_, record) => {
        const daysLeft = moment(record.end_date).diff(moment().startOf('day'), 'days');
        return (
          <div>
            <Text>{moment(record.end_date).format('DD MMM YYYY')}</Text>
            <br />
            <Text type={daysLeft <= 7 && daysLeft >= 0 ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {daysLeft < 0 ? 'Ended' : daysLeft === 0 ? 'Ends today' : `${daysLeft} days left`}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Name',
      key: 'name',
      width: 180,
      sorter: (a, b) => (a.firstname || '').localeCompare(b.firstname || ''),
      render: (_, record) => (
        <div>
          <Text strong>{record.firstname} {record.lastname}</Text>
          {record.nickname && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{record.nickname}</Text>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Course',
      key: 'course',
      width: 200,
      render: (_, record) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {record.class_code || '-'}
        </div>
      ),
    },
    {
      title: 'Invoice Date',
      key: 'invoice_date',
      width: 120,
      sorter: (a, b) => a.invoice_date.localeCompare(b.invoice_date),
      render: (_, record) => moment(record.invoice_date).format('DD MMM YYYY'),
    },
    {
      title: 'Latest Fee',
      key: 'latest_fee',
      width: 110,
      align: 'right',
      render: (_, record) => record.latest_fee != null
        ? Number(record.latest_fee).toLocaleString(undefined, { maximumFractionDigits: 2 })
        : '-',
    },
    {
      title: 'Invoice',
      key: 'invoice',
      width: 150,
      render: (_, record) => renderSelect(record, 'invoice', 'invoice_status', INVOICE_OPTIONS, 'Invoice'),
    },
    {
      title: 'Status',
      key: 'status',
      width: 160,
      render: (_, record) => renderSelect(record, 'status', 'status', STATUS_OPTIONS, 'Status'),
    },
    {
      title: 'Evaluation',
      key: 'evaluation',
      width: 150,
      render: (_, record) => renderSelect(record, 'evaluation', 'evaluation_status', EVALUATION_OPTIONS, 'Evaluation'),
    },
    {
      title: 'Remark',
      key: 'remark',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <RemarkCell value={record.remark} onSave={(val) => saveField(record, 'remark', val)} />
          <ChangedBy name={record.remark_by_name} at={record.remark_at} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <style>{`
        .course-end-select .ant-select-selector {
          background: var(--select-bg) !important;
          border-color: var(--select-color) !important;
        }
      `}</style>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              <BookOutlined /> Course End Tracker
            </Title>
            <Text type="secondary">
              Shows courses from 21 days before the end date (invoice date) onwards
            </Text>
          </div>
          <Space wrap>
            {userRole === "superadmin" && (
              <Select
                placeholder={t("Select Branch")}
                style={{ width: 180 }}
                allowClear
                value={selectedBranch}
                onChange={setSelectedBranch}
                options={branches.map(b => ({ value: b.id, label: b.name }))}
              />
            )}
            <Select
              placeholder="Status"
              style={{ width: 150 }}
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'none', label: 'No status yet' },
                ...STATUS_OPTIONS.map(o => ({ value: o.value, label: o.label })),
              ]}
            />
            <Select
              placeholder="Invoice"
              style={{ width: 150 }}
              allowClear
              value={invoiceFilter}
              onChange={setInvoiceFilter}
              options={[
                { value: 'none', label: 'No invoice status' },
                ...INVOICE_OPTIONS.map(o => ({ value: o.value, label: o.label })),
              ]}
            />
            <Select
              placeholder="Evaluation"
              style={{ width: 160 }}
              allowClear
              value={evaluationFilter}
              onChange={setEvaluationFilter}
              options={[
                { value: 'none', label: 'No evaluation yet' },
                ...EVALUATION_OPTIONS.map(o => ({ value: o.value, label: o.label })),
              ]}
            />
            <RangePicker
              placeholder={['End date from', 'End date to']}
              format="DD MMM YYYY"
              onChange={(dates) => setEndDateRange(
                dates ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : null
              )}
            />
            <Select
              style={{ width: 190 }}
              value={lookbackDays}
              onChange={setLookbackDays}
              options={LOOKBACK_OPTIONS}
              disabled={!!endDateRange}
            />
            <Input
              placeholder="Search name, course, remark..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 240 }}
              prefix={<SearchOutlined />}
              allowClear
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchCourseEnd}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredRows}
          loading={loading}
          rowKey={rowKey}
          size="small"
          bordered
          scroll={{ x: 1500 }}
          pagination={{
            defaultPageSize: 100,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
          }}
        />
      </Card>
    </div>
  );
};

export default withRoleAccess(StudentClassesReminder, ['admin', 'superadmin']);
